import { useState, useRef, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';

const FileUpload = ({
  onFileSelect,
  onFileRemove,
  accept = {},
  maxSize = 10 * 1024 * 1024, // 10MB default
  maxFiles = 1,
  multiple = false,
  disabled = false,
  className = '',
  children,
  showPreview = true,
  uploadedFiles = [],
  loading = false,
  error = null,
  helperText = '',
  required = false,
  label = 'Upload Files'
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      const errors = rejectedFiles.map(file => ({
        file: file.file.name,
        errors: file.errors.map(err => err.message)
      }));
    }

    if (acceptedFiles.length > 0) {
      onFileSelect(acceptedFiles);
    }
  }, [onFileSelect]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxSize,
    maxFiles,
    multiple,
    disabled: disabled || loading,
    onDragEnter: () => setDragActive(true),
    onDragLeave: () => setDragActive(false),
  });

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) {
      return (
        <svg className="w-8 h-8 text-jet-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
        </svg>
      );
    } else if (fileType === 'application/pdf') {
      return (
        <svg className="w-8 h-8 text-jet-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
        </svg>
      );
    } else {
      return (
        <svg className="w-8 h-8 text-jet-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
        </svg>
      );
    }
  };

  const handleRemoveFile = (fileIndex) => {
    if (onFileRemove) {
      onFileRemove(fileIndex);
    }
  };

  return (
    <div className={`file-upload-container ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-jet-gray-700 mb-2">
          {label} {required && <span className="text-jet-red-500">*</span>}
        </label>
      )}

      {/* Upload Area */}
      <div
        {...getRootProps()}
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
          ${isDragActive || dragActive 
            ? 'border-jet-blue-400 bg-jet-blue-50' 
            : error 
              ? 'border-jet-red-300 bg-jet-red-50'
              : 'border-jet-gray-300 hover:border-jet-gray-400 bg-jet-gray-50'
          }
          ${disabled || loading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input {...getInputProps()} ref={fileInputRef} />
        
        {loading ? (
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-jet-blue-500 mb-2"></div>
            <p className="text-sm text-jet-gray-600">Uploading...</p>
          </div>
        ) : children ? (
          children
        ) : (
          <div className="flex flex-col items-center">
            <svg className="w-12 h-12 text-jet-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
            </svg>
            <p className="text-sm text-jet-gray-600 mb-1">
              {isDragActive ? 'Drop files here' : 'Drag & drop files here, or click to select'}
            </p>
            <p className="text-xs text-jet-gray-500">
              {Object.keys(accept).length > 0 && `Accepted: ${Object.keys(accept).join(', ')}`}
              {maxSize && ` • Max size: ${formatFileSize(maxSize)}`}
              {maxFiles > 1 && ` • Max files: ${maxFiles}`}
            </p>
          </div>
        )}
      </div>

      {/* Helper Text */}
      {helperText && (
        <p className="mt-1 text-xs text-jet-gray-500">{helperText}</p>
      )}

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-sm text-jet-red-600">{error}</p>
      )}

      {/* File Preview */}
      {showPreview && uploadedFiles.length > 0 && (
        <div className="mt-4 space-y-2">
          <h4 className="text-sm font-medium text-jet-gray-700">Uploaded Files</h4>
          {uploadedFiles.map((file, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-white border border-jet-gray-200 rounded-lg">
              <div className="flex items-center space-x-3">
                {getFileIcon(file.type || file.mimeType)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-jet-gray-900 truncate">
                    {file.name || file.originalName}
                  </p>
                  <p className="text-xs text-jet-gray-500">
                    {formatFileSize(file.size)} • {file.type || file.mimeType}
                  </p>
                  {uploadProgress[index] !== undefined && uploadProgress[index] < 100 && (
                    <div className="mt-1">
                      <div className="bg-jet-gray-200 rounded-full h-1">
                        <div 
                          className="bg-jet-blue-500 h-1 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress[index]}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                {file.url && (
                  <button
                    type="button"
                    onClick={() => window.open(file.url, '_blank')}
                    className="text-jet-blue-600 hover:text-jet-blue-700 text-sm"
                  >
                    View
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleRemoveFile(index)}
                  className="text-jet-red-600 hover:text-jet-red-700 text-sm"
                  disabled={loading}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUpload;